import React, { useCallback, useEffect, useState } from 'react';
import { ColumnCellTemplateData } from 'devextreme/ui/data_grid';
import { SysIResultados } from '../../../../../types/types.orders';
import { Button, Tooltip } from 'devextreme-react';
import { useAppSelector, useAppDispatch } from '../../../../../contexts/hooks';
import { resGridConfidential, resGridImageResult, resGridTemplateEmpty, ordPending, agarMicrobiology } from '../../../../../images/icons';
import moment from 'moment';
import { useApiFunctionsResult } from '../Functions/Results';
import { showModalTemplates, showModalImages, showModalMicrobiology } from '../../../../../contexts/slices/orders';
import { ResultTypeResult } from '../../../../../types/types.analitos';

function CellComponent({ data }: { data: ColumnCellTemplateData<SysIResultados, number> }): React.ReactElement {
  const { data: rowData, value } = data;

  const [alteredData, setAlteredData] = useState('');
  const [alarmAndAbsurdData, setAlarmAndAbsurdData] = useState('');
  const ModuleTracking: boolean = JSON.parse(useAppSelector((x) => x.configuration.Data?.Config_General.find((x) => x.Cgn_Nombre === 'ModuleTracking')?.Cgn_Resultado) || 'false');
  const Prm_Res_Conf = useAppSelector((x) => x.userInfo.Permissions?.Prm_Res_Conf);
  const Prm_ResC = useAppSelector((x) => x.userInfo.Permissions?.Prm_ResC) || false;
  const ModuleAlarms: boolean = JSON.parse(useAppSelector((x) => x.configuration.Data?.Config_General.find((x) => x.Cgn_Nombre === 'ModuleAlarms')?.Cgn_Resultado) || 'false');
  const { checkAlarmAndAburd } = useApiFunctionsResult();
  const dispatch = useAppDispatch();

  // Función para sanitizar cualquier valor que pueda ser un objeto
  const sanitizeForDisplay = (val: any): string => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
  };

  const checkAlterItem = () => {
    if (rowData?.Analito.An_Tr_Id === ResultTypeResult.NUMERICO || rowData?.Analito.An_Tr_Id === ResultTypeResult.FORMULA) {
      if (rowData.Res_ValRefMin === null || rowData.Res_ValRefMax === null) {
        setAlteredData('');
        return;
      }
      if (!rowData?.Res_Resultado || (!rowData.Res_ValRefMin && !rowData.Res_ValRefMax) || isNaN(parseFloat(rowData.Res_Resultado)) || (rowData.Res_ValRefMin === 0 && rowData.Res_ValRefMax === 0)) {
        setAlteredData('');
        return;
      }
      if (parseFloat(rowData.Res_Resultado) < rowData.Res_ValRefMin) {
        return setAlteredData('result-error-lbl');
      }
      if (parseFloat(rowData.Res_Resultado) > rowData.Res_ValRefMax) {
        return setAlteredData('result-error-lbl');
      }
    }
    if (rowData?.Analito.An_Tr_Id === ResultTypeResult.TEXTUAL && rowData.Analito.An_ValRefText && rowData.Analito.An_ValRefText?.toUpperCase().trim() !== rowData.Res_Resultado?.toUpperCase().trim()) {
      //Validar que esta palabra no tenga guiones, espacios u otros caracteres especiales si si los tiene no marcar error
      const hasSpecialChars = /[^\w\s]/.test(rowData.Analito.An_ValRefText);
      if (hasSpecialChars) {
        setAlteredData('');
        return;
      }

      return setAlteredData('result-error-lbl');
    }

    return setAlteredData('');
  };

  const handleModalTemplate = useCallback(() => {
    if (!rowData || !Prm_ResC) {
      return;
    }
    dispatch(showModalTemplates({ Visible: true, An_Id: rowData.Res_An_Id, An_Nombre: rowData.Analito.An_Nombre, Res_Id: rowData.Res_Id, Res_Estado: rowData.Res_Estado, Res_Resultado: rowData.Res_ResultadoPlantilla, An_Plantilla: rowData.Analito.An_Plantilla, Update: false, Pc_Id: rowData.Orden.Ord_Pc_Id }));
  }, []);

  const handleModalImage = useCallback(() => {
    if (!rowData || !Prm_ResC) {
      return;
    }
    dispatch(showModalImages({ Visible: true, An_Id: rowData.Res_An_Id, An_Nombre: rowData.Analito.An_Nombre, Res_Id: rowData.Res_Id, Res_Estado: rowData.Res_Estado, Res_Resultado: rowData.Res_ResultadoPlantilla, An_Plantilla: rowData.Analito.An_Plantilla, Update: false, Pc_Id: rowData.Orden.Ord_Pc_Id }));
  }, []);

  const handleModalMicrobiology = useCallback(() => {
    if (!rowData || !Prm_ResC) {
      return;
    }
    dispatch(
      showModalMicrobiology({
        Visible: true,
        Ord_Id: rowData.Res_Ord_Id,
        Es_Id: rowData.Res_Es_Id,
        Es_Nombre: rowData.Estudio.Es_Nombre,
        An_Id: rowData.Res_An_Id,
        An_Nombre: rowData.Analito.An_Nombre,
        Res_Id: rowData.Res_Id,
        Res_Estado: rowData.Res_Estado,
        Res_Resultado: rowData.Res_Resultado,
        Update: false,
        Pc_Id: rowData.Orden.Ord_Pc_Id,
      })
    );
  }, [rowData, Prm_ResC, dispatch]);

  useEffect(() => {
    checkAlterItem();
    //Primero se valida que el módulo de alarmas esté activo
    if (ModuleAlarms) {
      setAlarmAndAbsurdData(checkAlarmAndAburd(rowData as SysIResultados));
    }
  }, [value]);

  //Eliminamos el render primero para las muestras que no están trackeadas
  if ((ModuleTracking && !rowData?.Res_MuestraTrackedada) || !rowData?.Res_EstadoMuestra) {
    return (
      <div>
        <img src={ordPending} id={`NoTracking${rowData?.Res_Id}`} alt='Muestra sin tracking' />
        <Tooltip showEvent='mouseenter' hideEvent='mouseleave' position='right' target={`#NoTracking${rowData?.Res_Id}`}>
          {!rowData?.Res_EstadoMuestra ? 'Muestra sin confirmar' : 'Muestra sin tracking'}
        </Tooltip>
      </div>
    );
  }

  //Validamos los confidenciales
  if (rowData?.Estudio.Es_Confidencial && rowData.Res_Estado > 1 && !Prm_Res_Conf) {
    return (
      <div>
        <img src={resGridConfidential} id={`Confidential${rowData?.Res_Id}`} alt='Muestra sin tracking' />
        <Tooltip showEvent='mouseenter' hideEvent='mouseleave' position='right' target={`#Confidential${rowData?.Res_Id}`}>
          Resultado confidencial
        </Tooltip>
      </div>
    );
  }

  // Protección adicional: si el value es el objeto problemático, manejarlo aquí
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    // Si es un objeto con las claves problemáticas, renderizar como JSON
    const objectKeys = Object.keys(value);
    if (objectKeys.some((key) => key.includes('Superficiales') || key.includes('Parabasales'))) {
      return <div className={`custom-editorText ${alteredData}`}>{JSON.stringify(value)}</div>;
    }
  }

  switch (rowData?.Analito.An_Tr_Id) {
    case ResultTypeResult.NUMERICO: //Numérico
    case ResultTypeResult.FORMULA: //Fórmula
      return <div className={`${alteredData} ${alarmAndAbsurdData}`}>{sanitizeForDisplay(value)}</div>;
    case ResultTypeResult.TEXTUAL: //Textual
    case ResultTypeResult.LISTA: //Lista
    case ResultTypeResult.AUTOCOMPLETAR: //Autocompletar
      return <div className={`custom-editorText ${alteredData}`}>{sanitizeForDisplay(value)}</div>;
    case ResultTypeResult.PLANTILLA: //Plantilla
      let result = [];
      let isTemplate = false;
      if (rowData.Res_Resultado) {
        try {
          result = JSON.parse(rowData.Res_Resultado);
          isTemplate = true;
        } catch (error) {
          isTemplate = false;
          result = [];
        }
        if (!isTemplate) {
          return <div className={`custom-editorText ${alteredData}`}>{sanitizeForDisplay(value)}</div>;
        }
      }

      return (
        <div>
          <Button id={`pltAn${rowData.Res_An_Id}`} icon={resGridTemplateEmpty} onClick={handleModalTemplate} />
          <Tooltip target={`#pltAn${rowData.Res_An_Id}`} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
            <div>
              {Array.isArray(result) &&
                result.map((x: any, index: number) => {
                  const groupKey = sanitizeForDisplay(x.Group) || `group-${index}`;
                  const groupTitle = sanitizeForDisplay(x.Group) || 'Sin nombre';

                  return React.createElement('div', { key: groupKey, className: 'tooltip-content' }, [
                    React.createElement('h6', { key: `title-${index}`, className: 'tooltip-title' }, groupTitle),
                    ...(Array.isArray(x.Items)
                      ? x.Items.map((y: any, itemIndex: number) => {
                          const itemKey = sanitizeForDisplay(y.Key) || `item-${index}-${itemIndex}`;
                          const itemLabel = sanitizeForDisplay(y.Label) || '';

                          // Si y.Value es un objeto, renderizar cada propiedad
                          if (typeof y.Value === 'object' && y.Value !== null) {
                            return React.createElement('div', { key: itemKey, className: 'tooltip-item' }, [
                              React.createElement('strong', { key: `label-${itemKey}` }, `  ${itemLabel}:`),
                              ...Object.entries(y.Value).map(([key, value], entryIndex: number) => {
                                const entryKey = sanitizeForDisplay(key) || `entry-${index}-${itemIndex}-${entryIndex}`;
                                const entryLabel = sanitizeForDisplay(key) || '';
                                const entryValue = sanitizeForDisplay(value);

                                return React.createElement(
                                  'p',
                                  {
                                    key: entryKey,
                                    style: { marginLeft: '20px', margin: '2px 0' },
                                  },
                                  [React.createElement('strong', { key: `entry-label-${entryKey}` }, `${entryLabel.trim()}: `), React.createElement('span', { key: `entry-value-${entryKey}` }, entryValue)]
                                );
                              }),
                            ]);
                          } else {
                            // Si y.Value es un string o número, renderizar normalmente
                            const itemValue = sanitizeForDisplay(y.Value);
                            return React.createElement('p', { key: itemKey, className: 'tooltip-item' }, [React.createElement('strong', { key: `item-label-${itemKey}` }, `  ${itemLabel}: `), React.createElement('span', { key: `item-value-${itemKey}` }, itemValue)]);
                          }
                        })
                      : []),
                  ]);
                })}
            </div>
          </Tooltip>
        </div>
      );
    case ResultTypeResult.FECHA:
      return <div>{value && moment(value).format('YYYY/MM/DD')}</div>;
    case ResultTypeResult.TIEMPO:
      return <div>{value && moment(value).format('HH:mm:SS a')}</div>;
    case ResultTypeResult.IMAGEN:
      return (
        <div>
          <Button icon={resGridImageResult} onClick={handleModalImage} />
        </div>
      );

    case ResultTypeResult.MICROBIOLOGIA:
      return (
        <div>
          <Button icon={agarMicrobiology} onClick={handleModalMicrobiology} disabled={!Prm_ResC} />
        </div>
      );

    default:
      return <div>unknown data type: {sanitizeForDisplay(value)}</div>;
  }
}

export default CellComponent;
